import { useState, useEffect, useRef, Fragment } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useSEO } from "../Utils/useSEO";
import { useTheme } from "../Utils/useTheme";
import { ScrollReveal, ScrollRevealItem } from "../components/ScrollReveal";
import {
  Check,
  X,
  HelpCircle,
  Sparkles,
  ArrowRight,
  Percent,
  Clock,
  TrendingUp,
  DollarSign,
  Users,
  ShieldCheck,
  Truck,
  Bot,
  Layers,
  Flame,
  ChevronDown,
  ChevronUp,
  Cpu,
  FileCheck2,
  Calendar,
  Zap
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function PricingPage() {
  useSEO({
    title: "Pricing Plans // Sourcing & Procurement Solutions",
    description: "Select the perfect enterprise stack for your business. Transparent tier structures from startup builders to global supply chains."
  });

  const { theme } = useTheme();
  const isDark = theme !== "light";
  const [billingPeriod, setBillingPeriod] = useState("annual");

  // Accordion active index
  const [activeFaq, setActiveFaq] = useState(null);

  // ROI Calculator states
  const [suppliers, setSuppliers] = useState(120);
  const [invoices, setInvoices] = useState(600);
  const [purchaseOrders, setPurchaseOrders] = useState(450);
  const [usersCount, setUsersCount] = useState(15);

  // Animated Stats trigger
  const [stats, setStats] = useState({
    onboarding: 0,
    manualWork: 0,
    accuracy: 0,
    inventory: 0,
  });
  const statsSectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          const interval = setInterval(() => {
            setStats((prev) => {
              const nextOnboarding = prev.onboarding < 50 ? prev.onboarding + 1 : 50;
              const nextManual = prev.manualWork < 80 ? prev.manualWork + 2 : 80;
              const nextAccuracy = prev.accuracy < 95 ? prev.accuracy + 2 : 95;
              const nextInventory = prev.inventory < 30 ? prev.inventory + 1 : 30;

              if (
                nextOnboarding === 50 &&
                nextManual === 80 &&
                nextAccuracy === 95 &&
                nextInventory === 30
              ) {
                clearInterval(interval);
              }
              return {
                onboarding: nextOnboarding,
                manualWork: nextManual,
                accuracy: nextAccuracy,
                inventory: nextInventory,
              };
            });
          }, 25);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (statsSectionRef.current) {
      observer.observe(statsSectionRef.current);
    }
    return () => observer.disconnect();
  }, []);

  // ROI Calculations
  // Baseline assumption metrics
  // Onboarding saves 2 hours per supplier (amortized over year, say / 12 per month)
  // AP automation saves 0.75 hours per invoice (3-way auto-matching vs manual review)
  // Procurement saves 0.3 hours per PO
  // Standard user hourly cost = ₹850
  const hoursSavedOnboarding = (suppliers * 2) / 12;
  const hoursSavedInvoices = invoices * 0.75;
  const hoursSavedPOs = purchaseOrders * 0.3;
  const totalHoursSaved = Math.round(hoursSavedOnboarding + hoursSavedInvoices + hoursSavedPOs);

  const totalCostSavings = Math.round(totalHoursSaved * 950 + invoices * 180); // Extra savings from error reduction
  const efficiencyImprovement = Math.min(88, Math.round(40 + (purchaseOrders / 80) + (suppliers / 25)));

  // ROI logic relative to platform cost
  const estimatedPlatformCost = billingPeriod === "annual" ? 39999 * 12 : 49999 * 12; // business plan as baseline
  const yearlySavings = totalCostSavings * 12;
  const roiPercentage = Math.max(140, Math.round((yearlySavings / estimatedPlatformCost) * 100));

  const formatCurrency = (val) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(val);
  };

  const faqData = [
    {
      q: "Can I upgrade later?",
      a: "Yes! You can upgrade or downgrade your plan at any time. If you decide to upgrade mid-billing cycle, we will prorate the charges based on your usage.",
    },
    {
      q: "Do you offer free trials?",
      a: "Absolutely. We offer a 14-day free trial on our Starter and Professional plans. No credit card is required to sign up and test the fundamental features.",
    },
    {
      q: "Do you integrate with SAP?",
      a: "Yes. Our Business and Enterprise plans offer direct, real-time bi-directional integration with SAP ERP (including S/4HANA), Oracle Cloud, and Microsoft Dynamics 365.",
    },
    {
      q: "Do you support multiple companies?",
      a: "Our Enterprise plan supports multi-company and multi-country operational structures, allowing you to segment data and compliance policies across parent and subsidiary groups.",
    },
    {
      q: "Can pricing be customized?",
      a: "Yes, for our Enterprise customers, we offer custom billing metrics, private cloud deployment hosting rates, custom SLA options, and tiered vendor-based pricing.",
    },
    {
      q: "Do you offer implementation services?",
      a: "Yes. We offer fully-managed migration and go-live assistance. For Business and Enterprise accounts, a dedicated Implementation Specialist will guide your team through the entire setup process.",
    },
    {
      q: "What payment methods are supported?",
      a: "We accept all major credit cards, bank wire transfers (NEFT/RTGS/IMPS), corporate billing arrangements, and automated clearing house (ACH) network setups.",
    },
  ];

  const plans = [
    {
      name: "STARTER",
      bestFor: "Small Businesses",
      priceMonthly: 9999,
      priceAnnualMonthly: 7999,
      cta: "Start Free Trial",
      ctaLink: "https://supplyspot.ai/",
      gradient: "from-orange-500 to-amber-500",
      ctaType: "secondary",
      features: [
        "Up to 50 Suppliers",
        "Supplier Onboarding",
        "Supplier Database",
        "Document Management",
        "Compliance Verification",
        "RFQ Management",
        "Purchase Order Collaboration",
        "Email Notifications",
        "Standard Reports",
        "5 Users",
      ],
      plusText: null,
    },
    {
      name: "PROFESSIONAL",
      bestFor: "Growing Operations",
      priceMonthly: 24999,
      priceAnnualMonthly: 19999,
      cta: "Start Free Trial",
      ctaLink: "https://supplyspot.ai/",
      popular: true,
      gradient: "from-orange-500 via-amber-500 to-amber-400",
      ctaType: "primary",
      features: [
        "Unlimited Suppliers",
        "Vendor Management",
        "Contract Management",
        "Smart Delivery Scheduling",
        "JIT Logistics",
        "GPS Tracking",
        "Dock Scheduling",
        "QR/RFID Check-In",
        "Supplier Scorecards",
        "Analytics Dashboard",
        "API Access",
        "25 Users",
      ],
      plusText: "Starter Plan",
    },
    {
      name: "BUSINESS",
      bestFor: "Mid-Market & Large Enterprise",
      priceMonthly: 49999,
      priceAnnualMonthly: 39999,
      cta: "Book Demo",
      ctaLink: "#contact",
      gradient: "from-orange-500 to-amber-400",
      ctaType: "primary",
      features: [
        "Accounts Payable Automation",
        "AI Invoice Capture",
        "AI 3-Way Matching",
        "Payment Tracking",
        "Advanced Analytics",
        "Spend Analysis",
        "AI Risk Scoring",
        "Procurement Copilot",
        "ERP Integration",
        "SAP Integration",
        "SSO Authentication",
        "Unlimited Users",
      ],
      plusText: "Professional Plan",
    },
    {
      name: "ENTERPRISE",
      bestFor: "Global Conglomerates",
      priceMonthly: "Custom",
      priceAnnualMonthly: "Custom",
      cta: "Contact Sales",
      ctaLink: "#contact",
      gradient: "from-orange-600 to-amber-600",
      ctaType: "secondary",
      features: [
        "Multi-Company Support",
        "Multi-Country Support",
        "Custom Workflows",
        "White Label Solution",
        "Dedicated Account Manager",
        "Priority Support",
        "Advanced Security",
        "Custom Integrations",
        "Private Cloud Deployment",
        "SLA Guarantee",
        "On-Premise Option",
      ],
      plusText: "Business Plan",
    },
  ];

  const categories = [
    {
      name: "Supplier Management",
      features: [
        { name: "Supplier Database Limit", starter: "50 Suppliers", professional: "Unlimited", business: "Unlimited", enterprise: "Unlimited" },
        { name: "Self-Service Onboarding Portal", starter: true, professional: true, business: true, enterprise: true },
        { name: "Compliance Verification", starter: true, professional: true, business: true, enterprise: true },
        { name: "Multi-Company Partitioning", starter: false, professional: false, business: false, enterprise: true },
      ]
    },
    {
      name: "Vendor Management",
      features: [
        { name: "Performance Scorecards", starter: false, professional: true, business: true, enterprise: true },
        { name: "Contract Management & Alerts", starter: false, professional: true, business: true, enterprise: true },
        { name: "White Label Supplier Experience", starter: false, professional: false, business: false, enterprise: true },
      ]
    },
    {
      name: "Procurement & RFQ",
      features: [
        { name: "RFQ Management Suite", starter: true, professional: true, business: true, enterprise: true },
        { name: "Purchase Order Collaboration", starter: true, professional: true, business: true, enterprise: true },
        { name: "Smart Threshold Ordering", starter: false, professional: false, business: true, enterprise: true },
      ]
    },
    {
      name: "Smart Delivery & JIT",
      features: [
        { name: "GPS Transit Telemetry", starter: false, professional: true, business: true, enterprise: true },
        { name: "Dynamic Dock Scheduling", starter: false, professional: true, business: true, enterprise: true },
        { name: "QR/RFID Gate Scanning integration", starter: false, professional: true, business: true, enterprise: true },
      ]
    },
    {
      name: "Accounts Payable Automation",
      features: [
        { name: "Digital Document Upload", starter: true, professional: true, business: true, enterprise: true },
        { name: "AI 3-Way Order Matching", starter: false, professional: false, business: true, enterprise: true },
        { name: "Bank Ledger Escalation Setup", starter: false, professional: false, business: true, enterprise: true },
      ]
    },
    {
      name: "AI & Co-Pilot Core",
      features: [
        { name: "Aether AI Assistant Integration", starter: false, professional: false, business: true, enterprise: true },
        { name: "Compliance Geopolitical Risk Scoring", starter: false, professional: false, business: true, enterprise: true },
        { name: "Predictive Cargo Latency AI", starter: false, professional: false, business: true, enterprise: true },
      ]
    },
    {
      name: "Integrations & API",
      features: [
        { name: "Developer API Access", starter: false, professional: true, business: true, enterprise: true },
        { name: "SAP, Oracle, ERP Integrations", starter: false, professional: false, business: true, enterprise: true },
        { name: "Custom Database Adapters", starter: false, professional: false, business: false, enterprise: true },
      ]
    },
    {
      name: "Support & SLA",
      features: [
        { name: "Support Tier", starter: "Email Support", professional: "Priority Support", business: "Dedicated Success Manager", enterprise: "24x7 Support Team" },
        { name: "SLA Guarantee", starter: false, professional: false, business: false, enterprise: true },
        { name: "On-Premise Deployment Option", starter: false, professional: false, business: false, enterprise: true },
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-bg-primary text-text-primary overflow-x-hidden font-sans pb-0 relative selection:bg-brand-orange/30 selection:text-text-primary transition-colors duration-300">
      <Navbar theme="navy" />

      {/* Background Cyber Grid */}
      <div className="absolute inset-0 bg-grid-pattern-50 pointer-events-none -z-20" />

      {/* Background Amber & Gold Orbs */}
      <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-brand-orange/10 rounded-full blur-[120px] -z-10 pointer-events-none" />
      <div className="absolute top-1/3 left-0 w-[600px] h-[600px] bg-brand-amber/10 rounded-full blur-[140px] -z-10 pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-brand-gold/5 rounded-full blur-[120px] -z-10 pointer-events-none" />

      {/* HERO SECTION */}
      <section className="relative pt-36 pb-20 px-6 max-w-7xl mx-auto text-center overflow-hidden">
        {/* Animated Digital Supply Chain Grid Visual */}
        <div className="absolute inset-0 max-w-lg mx-auto h-72 opacity-15 pointer-events-none -z-10 flex items-center justify-center">
          <svg className="w-full h-full text-brand-orange" viewBox="0 0 100 100" fill="none">
            <line x1="10" y1="20" x2="50" y2="40" stroke="currentColor" strokeWidth="0.5" strokeDasharray="2 2" />
            <line x1="50" y1="40" x2="90" y2="20" stroke="currentColor" strokeWidth="0.5" strokeDasharray="2 2" />
            <line x1="50" y1="40" x2="50" y2="80" stroke="currentColor" strokeWidth="0.5" />
            <line x1="10" y1="20" x2="90" y2="20" stroke="currentColor" strokeWidth="0.25" />
            <circle cx="10" cy="20" r="1.5" fill="var(--color-brand-gold)" />
            <circle cx="50" cy="40" r="2.5" fill="var(--color-brand-orange)" />
            <circle cx="90" cy="20" r="1.5" fill="var(--color-brand-gold)" />
            <circle cx="50" cy="80" r="2" fill="var(--color-brand-amber)" />

            {/* Pulsing signal node */}
            <circle cx="50" cy="40" r="6" stroke="var(--color-brand-orange)" strokeWidth="0.5" className="animate-ping" style={{ transformOrigin: "center" }} />
          </svg>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand-orange/20 bg-brand-orange/5 text-brand-orange text-xs font-semibold uppercase tracking-wider mb-6 shadow-sm shadow-brand-orange/5 animate-pulse"
        >
          <Sparkles className="w-3.5 h-3.5" />
          <span>FLEXIBLE ENTERPRISE PLANS</span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-text-primary leading-tight font-title"
        >
          Simple Pricing for <br />
          <span className="bg-gradient-to-r from-brand-orange via-brand-amber to-brand-gold bg-clip-text text-transparent drop-shadow-sm">
            Every Business
          </span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-text-secondary text-base md:text-lg max-w-2xl mx-auto mt-6 leading-relaxed"
        >
          Scale your supplier management, procurement, logistics, and accounts payable operations with a plan that fits your business.
        </motion.p>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="flex flex-wrap justify-center items-center gap-4 mt-8"
        >
          <Link
            to="https://supplyspot.ai/"
            className="px-6 py-3.5 font-bold text-xs tracking-wider text-white bg-gradient-to-r from-brand-orange to-brand-amber rounded-lg shadow-lg shadow-brand-orange/20 hover:scale-[1.02] active:scale-[0.98] transition-all hover:shadow-brand-amber/30 hover:brightness-110"
          >
            START FREE TRIAL
          </Link>
          <Link
            to="/contact"
            className="px-6 py-3.5 font-bold text-xs tracking-wider text-text-primary border border-brand-orange/20 hover:border-brand-orange/60 rounded-lg bg-bg-secondary/40 hover:text-brand-amber transition-all shadow-sm"
          >
            BOOK DEMO
          </Link>
        </motion.div>

        {/* Toggle billing */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-12"
        >
          <div className="bg-bg-secondary/40 backdrop-blur-sm p-1 rounded-full flex items-center border border-brand-orange/15 shadow-inner">
            <button
              onClick={() => setBillingPeriod("monthly")}
              className={`px-6 py-2 rounded-full text-xs font-bold transition-all ${billingPeriod === "monthly"
                  ? "bg-gradient-to-r from-brand-orange to-brand-amber text-white shadow-md"
                  : "text-text-secondary hover:text-text-primary"
                }`}
            >
              Monthly Billing
            </button>
            <button
              onClick={() => setBillingPeriod("annual")}
              className={`px-6 py-2 rounded-full text-xs font-bold transition-all relative ${billingPeriod === "annual"
                  ? "bg-gradient-to-r from-brand-orange to-brand-amber text-white shadow-md"
                  : "text-text-secondary hover:text-text-primary"
                }`}
            >
              Annual Billing
            </button>
          </div>
          <span className="text-xs font-bold text-brand-amber bg-brand-orange/10 border border-brand-orange/20 px-3 py-1 rounded-full animate-pulse">
            Save 20% with Annual Billing
          </span>
        </motion.div>
      </section>

      {/* PRICING CARDS SECTION */}
      <section className="px-6 py-12 max-w-7xl mx-auto relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {plans.map((plan, idx) => {
            const isCustom = typeof plan.priceMonthly === "string";
            const price = billingPeriod === "annual" ? plan.priceAnnualMonthly : plan.priceMonthly;
            const formattedPrice = isCustom ? "Custom" : formatCurrency(price);

            return (
              <motion.div
                key={plan.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className={`rounded-3xl p-8 border relative flex flex-col justify-between transition-all duration-300 group ${plan.popular
                    ? "border-brand-orange bg-bg-secondary/80 backdrop-blur-[20px] shadow-glow-orange scale-105 z-20"
                    : "glass-panel border-brand-orange/15 hover:border-brand-amber/40 hover:-translate-y-1 hover:shadow-lg"
                  }`}
              >
                {/* Popular highlight indicator */}
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-brand-orange to-brand-amber text-white text-[10px] font-bold px-4 py-1.5 rounded-full tracking-widest shadow-md flex items-center gap-1.5 border border-yellow-400/30">
                    <Flame className="w-3.5 h-3.5 fill-white" />
                    MOST POPULAR
                  </div>
                )}

                <div>
                  {/* Plan header */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-xs font-bold tracking-widest text-text-muted uppercase font-title">{plan.name}</h3>
                      {plan.popular && (
                        <span className="text-[9px] text-brand-orange border border-brand-orange/20 px-2 py-0.5 rounded bg-brand-orange/5 font-bold">GOLD BADGE</span>
                      )}
                    </div>
                    <p className="text-text-secondary text-xs mt-1.5">{plan.bestFor}</p>
                    <div className="mt-4 flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-text-primary">{formattedPrice}</span>
                      {!isCustom && (
                        <span className="text-text-secondary text-xs font-semibold">/month</span>
                      )}
                    </div>
                    {billingPeriod === "annual" && !isCustom && (
                      <span className="text-[10px] text-brand-amber font-bold block mt-1">
                        Billed annually ({formatCurrency(price * 12)}/year)
                      </span>
                    )}
                  </div>

                  <hr className="border-brand-orange/10 mb-6" />

                  {/* Plan CTA */}
                  <Link
                    to={plan.ctaLink}
                    className={`w-full py-3 px-4 rounded-xl text-center text-xs font-bold tracking-wider block transition-all duration-300 mb-6 ${plan.ctaType === "primary"
                        ? "bg-brand-orange hover:bg-brand-amber text-white shadow-md shadow-brand-orange/15 hover:shadow-brand-amber/30 hover:scale-[1.02] active:scale-[0.98]"
                        : "bg-transparent text-brand-orange border border-brand-orange/30 hover:border-brand-amber hover:text-brand-amber hover:bg-brand-orange/5"
                      }`}
                  >
                    {plan.cta}
                  </Link>

                  {/* Feature header */}
                  <div className="mb-4">
                    {plan.plusText ? (
                      <p className="text-[11px] font-bold text-text-primary uppercase tracking-wider mb-2">
                        Everything in {plan.plusText} Plus:
                      </p>
                    ) : (
                      <p className="text-[11px] font-bold text-text-primary uppercase tracking-wider mb-2">
                        Key Deliverables Included:
                      </p>
                    )}
                  </div>

                  {/* Features list */}
                  <ul className="flex flex-col gap-3 text-xs text-text-secondary">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2.5">
                        <Check className="w-4 h-4 text-brand-amber flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* ROI CALCULATOR SECTION */}
      <section className="px-6 py-20 bg-bg-secondary/15 border-y border-brand-orange/10 relative z-10">
        <div className="max-w-7xl mx-auto">
          <ScrollReveal className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand-orange/20 bg-brand-orange/5 text-brand-orange text-xs font-bold uppercase tracking-wider mb-6">
              <Percent className="w-3.5 h-3.5" />
              <span>INTERACTIVE ROI ESTIMATOR</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-text-primary font-title">
              Calculate Your Operational Savings
            </h2>
            <p className="text-text-secondary text-sm max-w-xl mx-auto">
              Adjust variables representing your logistics operations and see your estimated time, error rate, and invoice savings with Supply Spot.
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-stretch">
            {/* Input Sliders Panel */}
            <div className="lg:col-span-7 glass-panel p-8 rounded-3xl flex flex-col gap-6 justify-between shadow-xl shadow-black/25">
              <div>
                <h3 className="font-bold text-lg text-text-primary mb-6">Your Operational Parameters</h3>

                {/* Sliders */}
                <div className="flex flex-col gap-8">
                  {/* Slider 1: Suppliers */}
                  <div className="flex flex-col gap-2">
                    <div className="flex justify-between items-center text-sm">
                      <span className="font-semibold text-text-secondary flex items-center gap-2">
                        <Users className="w-4 h-4 text-brand-orange" />
                        Number of Active Suppliers
                      </span>
                      <span className="font-bold text-brand-amber bg-brand-orange/10 px-2 py-0.5 rounded-md border border-brand-orange/20">
                        {suppliers}
                      </span>
                    </div>
                    <input
                      type="range"
                      min="10"
                      max="1500"
                      step="10"
                      value={suppliers}
                      onChange={(e) => setSuppliers(parseInt(e.target.value))}
                      className="w-full h-2 bg-bg-secondary rounded-lg appearance-none cursor-pointer accent-brand-orange"
                    />
                  </div>

                  {/* Slider 2: Invoices */}
                  <div className="flex flex-col gap-2">
                    <div className="flex justify-between items-center text-sm">
                      <span className="font-semibold text-text-secondary flex items-center gap-2">
                        <FileCheck2 className="w-4 h-4 text-brand-orange" />
                        Invoices Managed Per Month
                      </span>
                      <span className="font-bold text-brand-amber bg-brand-orange/10 px-2 py-0.5 rounded-md border border-brand-orange/20">
                        {invoices}
                      </span>
                    </div>
                    <input
                      type="range"
                      min="20"
                      max="4000"
                      step="20"
                      value={invoices}
                      onChange={(e) => setInvoices(parseInt(e.target.value))}
                      className="w-full h-2 bg-bg-secondary rounded-lg appearance-none cursor-pointer accent-brand-orange"
                    />
                  </div>

                  {/* Slider 3: Purchase Orders */}
                  <div className="flex flex-col gap-2">
                    <div className="flex justify-between items-center text-sm">
                      <span className="font-semibold text-text-secondary flex items-center gap-2">
                        <Zap className="w-4 h-4 text-brand-orange" />
                        Purchase Orders Dispatched Per Month
                      </span>
                      <span className="font-bold text-brand-amber bg-brand-orange/10 px-2 py-0.5 rounded-md border border-brand-orange/20">
                        {purchaseOrders}
                      </span>
                    </div>
                    <input
                      type="range"
                      min="20"
                      max="4000"
                      step="20"
                      value={purchaseOrders}
                      onChange={(e) => setPurchaseOrders(parseInt(e.target.value))}
                      className="w-full h-2 bg-bg-secondary rounded-lg appearance-none cursor-pointer accent-brand-orange"
                    />
                  </div>

                  {/* Slider 4: Users */}
                  <div className="flex flex-col gap-2">
                    <div className="flex justify-between items-center text-sm">
                      <span className="font-semibold text-text-secondary flex items-center gap-2">
                        <Users className="w-4 h-4 text-brand-orange" />
                        Procurement & Accounting Users
                      </span>
                      <span className="font-bold text-brand-amber bg-brand-orange/10 px-2 py-0.5 rounded-md border border-brand-orange/20">
                        {usersCount}
                      </span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="150"
                      step="1"
                      value={usersCount}
                      onChange={(e) => setUsersCount(parseInt(e.target.value))}
                      className="w-full h-2 bg-bg-secondary rounded-lg appearance-none cursor-pointer accent-brand-orange"
                    />
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-brand-orange/10 flex items-center justify-between text-xs text-text-muted">
                <span>* Based on global industry average metrics.</span>
                <Link to="/contact" className="font-bold text-brand-orange hover:underline flex items-center gap-1">
                  Get custom analysis <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </div>

            {/* Calculations Dashboard Output */}
            <div className="lg:col-span-5 bg-bg-secondary/40 backdrop-blur-md border border-brand-orange/15 text-text-primary p-8 rounded-3xl flex flex-col justify-between relative overflow-hidden shadow-2xl">
              {/* Glass subtle glows */}
              <div className="absolute top-0 right-0 w-48 h-48 bg-brand-orange/10 rounded-full blur-3xl -z-10 pointer-events-none" />
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-brand-amber/5 rounded-full blur-3xl -z-10 pointer-events-none" />

              <div>
                <h3 className="font-bold text-lg mb-6 text-text-primary">Estimated Monthly Value</h3>

                <div className="flex flex-col gap-6">
                  {/* Time Saved Output */}
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-brand-orange/10 border border-brand-orange/20 flex items-center justify-center text-brand-orange">
                      <Clock className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-[10px] text-text-muted font-bold uppercase tracking-wider">Estimated Time Saved</div>
                      <div className="text-2xl font-black text-text-primary">{totalHoursSaved} <span className="text-xs text-text-muted font-bold">Hours/mo</span></div>
                    </div>
                  </div>

                  {/* Financial Savings Output */}
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-brand-amber/10 border border-brand-amber/20 flex items-center justify-center text-brand-amber">
                      <DollarSign className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-[10px] text-text-muted font-bold uppercase tracking-wider">Estimated Cost Savings</div>
                      <div className="text-2xl font-black text-text-primary">{formatCurrency(totalCostSavings)}<span className="text-xs text-text-muted font-bold">/mo</span></div>
                    </div>
                  </div>

                  {/* Efficiency Increase */}
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-brand-orange/10 border border-brand-orange/20 flex items-center justify-center text-brand-orange">
                      <TrendingUp className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-[10px] text-text-muted font-bold uppercase tracking-wider">Procurement Efficiency</div>
                      <div className="text-2xl font-black text-text-primary">+{efficiencyImprovement}%</div>
                    </div>
                  </div>

                  {/* ROI Score */}
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-brand-amber/10 border border-brand-amber/20 flex items-center justify-center text-brand-amber">
                      <Percent className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="text-[10px] text-text-muted font-bold uppercase tracking-wider">Projected Annual ROI</div>
                      <div className="text-2xl font-black text-brand-amber">{roiPercentage}%</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-10">
                <Link
                  to="/contact"
                  className="w-full py-4 bg-gradient-to-r from-brand-orange to-brand-amber text-white rounded-xl font-bold text-center text-xs tracking-wider block hover:scale-[1.02] active:scale-[0.98] transition-all shadow-lg shadow-brand-orange/20 hover:brightness-110 hover:shadow-brand-amber/30"
                >
                  CALCULATE YOUR SAVINGS
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURE COMPARISON TABLE */}
      <section className="px-6 py-20 max-w-7xl mx-auto relative z-10">
        <ScrollReveal className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand-orange/20 bg-brand-orange/5 text-brand-orange text-xs font-bold uppercase tracking-wider mb-6">
            <Layers className="w-3.5 h-3.5" />
            <span>DETAILED COMPARISON</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-text-primary font-title">
            Compare Feature Capabilities
          </h2>
          <p className="text-text-secondary text-sm">
            Evaluate key features across all plans to discover the perfect solution for your organization.
          </p>
        </ScrollReveal>

        {/* Responsive Table Wrapper */}
        <ScrollReveal className="w-full overflow-x-auto rounded-3xl glass-panel shadow-xl shadow-black/25">
          <table className="w-full border-collapse text-left min-w-[800px]">
            <thead>
              <tr className="border-b border-brand-orange/10 bg-bg-secondary/40">
                <th className="p-6 font-bold text-text-primary text-sm min-w-[280px]">Feature Capability</th>
                <th className="p-6 font-bold text-text-secondary text-center text-sm w-[15%]">STARTER</th>
                <th className="p-6 text-center text-sm w-[15%] bg-brand-orange/5 border-x border-brand-orange/15">
                  <div className="relative inline-block text-brand-orange font-bold">
                    PROFESSIONAL
                    <span className="absolute -top-6 left-1/2 -translate-x-1/2 bg-gradient-to-r from-brand-orange to-brand-amber text-white text-[8px] font-black px-1.5 py-0.5 rounded uppercase tracking-widest whitespace-nowrap border border-brand-amber/20">
                      POPULAR
                    </span>
                  </div>
                </th>
                <th className="p-6 font-bold text-text-secondary text-center text-sm w-[15%]">BUSINESS</th>
                <th className="p-6 font-bold text-text-secondary text-center text-sm w-[15%]">ENTERPRISE</th>
              </tr>
            </thead>

            <tbody>
              {categories.map((cat) => (
                <Fragment key={cat.name}>
                  {/* Category Header Row */}
                  <tr className="border-b border-brand-orange/10">
                    <td colSpan={5} className="p-4 font-bold text-xs tracking-wider text-brand-amber uppercase bg-bg-secondary/60">
                      {cat.name}
                    </td>
                  </tr>

                  {/* Feature Rows */}
                  {cat.features.map((feature) => (
                    <tr key={feature.name} className="border-b border-brand-orange/5 hover:bg-bg-secondary/30 transition-colors">
                      <td className="p-5 text-text-secondary text-sm font-semibold">
                        {feature.name}
                      </td>

                      {/* Starter Value */}
                      <td className="p-5 text-center text-sm">
                        {typeof feature.starter === "boolean" ? (
                          feature.starter ? (
                            <Check className="w-5 h-5 text-brand-orange mx-auto" />
                          ) : (
                            <X className="w-5 h-5 text-text-muted/30 mx-auto" />
                          )
                        ) : (
                          <span className="text-text-secondary font-semibold">{feature.starter}</span>
                        )}
                      </td>

                      {/* Professional Value */}
                      <td className="p-5 text-center text-sm bg-brand-orange/5 border-x border-brand-orange/15">
                        {typeof feature.professional === "boolean" ? (
                          feature.professional ? (
                            <Check className="w-5 h-5 text-brand-orange mx-auto" />
                          ) : (
                            <X className="w-5 h-5 text-text-muted/30 mx-auto" />
                          )
                        ) : (
                          <span className="text-brand-orange font-bold">{feature.professional}</span>
                        )}
                      </td>

                      {/* Business Value */}
                      <td className="p-5 text-center text-sm">
                        {typeof feature.business === "boolean" ? (
                          feature.business ? (
                            <Check className="w-5 h-5 text-brand-orange mx-auto" />
                          ) : (
                            <X className="w-5 h-5 text-text-muted/30 mx-auto" />
                          )
                        ) : (
                          <span className="text-text-secondary font-semibold">{feature.business}</span>
                        )}
                      </td>

                      {/* Enterprise Value */}
                      <td className="p-5 text-center text-sm">
                        {typeof feature.enterprise === "boolean" ? (
                          feature.enterprise ? (
                            <Check className="w-5 h-5 text-brand-orange mx-auto" />
                          ) : (
                            <X className="w-5 h-5 text-text-muted/30 mx-auto" />
                          )
                        ) : (
                          <span className="text-text-secondary font-semibold">{feature.enterprise}</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </Fragment>
              ))}
            </tbody>
          </table>
        </ScrollReveal>
      </section>

      {/* WHY CHOOSE SUPPLY SPOT SECTION */}
      <section ref={statsSectionRef} className="px-6 py-20 bg-bg-secondary/15 border-t border-brand-orange/10 relative z-10">
        <div className="max-w-7xl mx-auto">
          <ScrollReveal className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-text-primary font-title">
              Why Choose Supply Spot?
            </h2>
            <p className="text-text-secondary text-sm max-w-xl mx-auto">
              Our automated portal scales procurement channels, accelerates invoice releasing, and guarantees total document verification.
            </p>
          </ScrollReveal>

          <ScrollReveal staggerChildren={0.08} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Cards */}
            <ScrollRevealItem className="flex">
              <div className="glass-panel p-6 rounded-2xl flex flex-col gap-4 shadow-xl shadow-black/25 w-full">
                <div className="w-10 h-10 rounded-lg bg-brand-orange/10 border border-brand-orange/30 text-brand-orange flex items-center justify-center">
                  <DollarSign className="w-5 h-5" />
                </div>
                <h3 className="font-bold text-base text-text-primary">Reduce Procurement Costs</h3>
                <p className="text-text-muted text-xs leading-relaxed">
                  Unlock wholesale catalog pricing and minimize processing errors via autonomous RFQ engines.
                </p>
              </div>
            </ScrollRevealItem>

            <ScrollRevealItem className="flex">
              <div className="glass-panel p-6 rounded-2xl flex flex-col gap-4 shadow-xl shadow-black/25 w-full">
                <div className="w-10 h-10 rounded-lg bg-brand-orange/10 border border-brand-orange/30 text-brand-orange flex items-center justify-center">
                  <Users className="w-5 h-5" />
                </div>
                <h3 className="font-bold text-base text-text-primary">Automate Supplier Management</h3>
                <p className="text-text-muted text-xs leading-relaxed">
                  Maintain high standard profiles, automate document expiration emails, and reduce security vetting.
                </p>
              </div>
            </ScrollRevealItem>

            <ScrollRevealItem className="flex">
              <div className="glass-panel p-6 rounded-2xl flex flex-col gap-4 shadow-xl shadow-black/25 w-full">
                <div className="w-10 h-10 rounded-lg bg-brand-orange/10 border border-brand-orange/30 text-brand-orange flex items-center justify-center">
                  <Truck className="w-5 h-5" />
                </div>
                <h3 className="font-bold text-base text-text-primary">Improve Delivery Performance</h3>
                <p className="text-text-muted text-xs leading-relaxed">
                  Connect incoming shipping with smart docking yards, GPS alerts, and RFID scanning tracking.
                </p>
              </div>
            </ScrollRevealItem>

            <ScrollRevealItem className="flex">
              <div className="glass-panel p-6 rounded-2xl flex flex-col gap-4 shadow-xl shadow-black/25 w-full">
                <div className="w-10 h-10 rounded-lg bg-brand-orange/10 border border-brand-orange/30 text-brand-orange flex items-center justify-center">
                  <Bot className="w-5 h-5" />
                </div>
                <h3 className="font-bold text-base text-text-primary">Accelerate Invoice Processing</h3>
                <p className="text-text-muted text-xs leading-relaxed">
                  3-way order mismatch matching scans documents instantly, queuing automated payments safely.
                </p>
              </div>
            </ScrollRevealItem>
          </ScrollReveal>

          {/* Animated Stats Section */}
          <ScrollReveal className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 pt-16 border-t border-brand-orange/10 text-center">
            <div>
              <div className="text-4xl font-bold text-brand-orange number-glow">{stats.onboarding}%</div>
              <div className="text-[10px] font-bold text-text-muted uppercase tracking-wider mt-2">50% Faster Onboarding</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-brand-orange number-glow">{stats.manualWork}%</div>
              <div className="text-[10px] font-bold text-text-muted uppercase tracking-wider mt-2">80% Reduced Manual Work</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-brand-orange number-glow">{stats.accuracy}%</div>
              <div className="text-[10px] font-bold text-text-muted uppercase tracking-wider mt-2">95% Invoice Accuracy</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-brand-orange number-glow">{stats.inventory}%</div>
              <div className="text-[10px] font-bold text-text-muted uppercase tracking-wider mt-2">30% Inventory Reduction</div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* IMPLEMENTATION & SUPPORT */}
      <section className="px-6 py-20 max-w-7xl mx-auto relative z-10">
        <ScrollReveal className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand-orange/20 bg-brand-orange/5 text-brand-orange text-xs font-bold uppercase tracking-wider mb-6">
            <Cpu className="w-3.5 h-3.5" />
            <span>SERVICE ARCHITECTURE</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-text-primary font-title">
            Enterprise Support & Deployment
          </h2>
          <p className="text-text-secondary text-sm max-w-xl mx-auto">
            From self-onboarding to guided multi-month private cloud custom structures, we ensure your organization achieves total JIT velocity.
          </p>
        </ScrollReveal>

        {/* Support Tier Overview */}
        <ScrollReveal staggerChildren={0.05} className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-16">
          <ScrollRevealItem className="flex">
            <div className="p-6 glass-panel rounded-2xl shadow-xl shadow-black/25 w-full text-left">
              <h3 className="font-bold text-xs text-text-muted uppercase tracking-wider mb-2">Starter Support</h3>
              <div className="text-sm font-bold text-text-primary mb-2">Email Support</div>
              <p className="text-text-secondary text-xs">Access to document database, user manuals, and email support desk.</p>
            </div>
          </ScrollRevealItem>
          <ScrollRevealItem className="flex">
            <div className="p-6 glass-panel rounded-2xl shadow-xl shadow-black/25 w-full text-left">
              <h3 className="font-bold text-xs text-text-muted uppercase tracking-wider mb-2">Professional Support</h3>
              <div className="text-sm font-bold text-text-primary mb-2">Priority Support</div>
              <p className="text-text-secondary text-xs">Accelerated queue response within 12 business hours, web ticket access.</p>
            </div>
          </ScrollRevealItem>
          <ScrollRevealItem className="flex">
            <div className="p-6 glass-panel !border-brand-orange/40 rounded-2xl shadow-[0_0_20px_rgba(249,115,22,0.15)] w-full text-left">
              <h3 className="font-bold text-xs text-brand-orange uppercase tracking-wider mb-2">Business Support</h3>
              <div className="text-sm font-bold text-text-primary mb-2">Dedicated Success Manager</div>
              <p className="text-text-secondary text-xs">Custom SLA parameters, live video screen audits, onboarding workshops.</p>
            </div>
          </ScrollRevealItem>
          <ScrollRevealItem className="flex">
            <div className="p-6 glass-panel rounded-2xl shadow-xl shadow-black/25 w-full text-left">
              <h3 className="font-bold text-xs text-text-muted uppercase tracking-wider mb-2">Enterprise Support</h3>
              <div className="text-sm font-bold text-text-primary mb-2">24x7 Dedicated Support Team</div>
              <p className="text-text-secondary text-xs">Unlimited phone escalation, emergency hotfix releases, custom ledger engineers.</p>
            </div>
          </ScrollRevealItem>
        </ScrollReveal>

        {/* Timeline details */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 pt-10 border-t border-brand-orange/10">
          <div className="flex gap-4">
            <div className="w-10 h-10 rounded-xl bg-brand-orange/10 border border-brand-orange/20 text-brand-orange flex items-center justify-center flex-shrink-0">
              <Calendar className="w-5 h-5" />
            </div>
            <div>
              <h4 className="font-bold text-sm text-text-primary mb-1">Implementation Timeline</h4>
              <p className="text-text-secondary text-xs leading-relaxed">
                Go-live setups range from 2 days for Starter to 4-6 weeks for custom global SAP setups.
              </p>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="w-10 h-10 rounded-xl bg-brand-orange/10 border border-brand-orange/20 text-brand-orange flex items-center justify-center flex-shrink-0">
              <Users className="w-5 h-5" />
            </div>
            <div>
              <h4 className="font-bold text-sm text-text-primary mb-1">Training Support</h4>
              <p className="text-text-secondary text-xs leading-relaxed">
                Custom dashboard video series, user checklist certification, and automated training modules.
              </p>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="w-10 h-10 rounded-xl bg-brand-orange/10 border border-brand-orange/20 text-brand-orange flex items-center justify-center flex-shrink-0">
              <Layers className="w-5 h-5" />
            </div>
            <div>
              <h4 className="font-bold text-sm text-text-primary mb-1">Migration Assistance</h4>
              <p className="text-text-secondary text-xs leading-relaxed">
                Easily import current Excel/CSV rosters, old audits, and legacy contact databases.
              </p>
            </div>
          </div>

          <div className="flex gap-4">
            <div className="w-10 h-10 rounded-xl bg-brand-orange/10 border border-brand-orange/20 text-brand-orange flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <div>
              <h4 className="font-bold text-sm text-text-primary mb-1">Go-Live Support</h4>
              <p className="text-text-secondary text-xs leading-relaxed">
                Pre-release validation checklists and developer setup review audits to ensure data integrity.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FREQUENTLY ASKED QUESTIONS SECTION */}
      <section className="px-6 py-20 bg-bg-secondary/15 border-t border-brand-orange/10 relative z-10">
        <div className="max-w-4xl mx-auto">
          <ScrollReveal className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-brand-orange/20 bg-brand-orange/5 text-brand-orange text-xs font-bold uppercase tracking-wider mb-6">
              <HelpCircle className="w-3.5 h-3.5" />
              <span>FAQ DIRECTORY</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 text-text-primary font-title">
              Frequently Asked Questions
            </h2>
            <p className="text-text-secondary text-sm">
              Answers to common billing, setup, integration, and security questions.
            </p>
          </ScrollReveal>

          <ScrollReveal className="flex flex-col gap-4">
            {faqData.map((faq, idx) => (
              <div
                key={idx}
                className="glass-panel rounded-2xl overflow-hidden transition-all shadow-xl shadow-black/20"
              >
                <button
                  onClick={() => setActiveFaq(activeFaq === idx ? null : idx)}
                  className="w-full p-6 text-left font-bold text-text-primary text-sm flex items-center justify-between gap-4 hover:text-brand-orange transition-colors"
                >
                  <span>{faq.q}</span>
                  {activeFaq === idx ? (
                    <ChevronUp className="w-5 h-5 text-brand-orange flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-text-muted flex-shrink-0" />
                  )}
                </button>

                <AnimatePresence initial={false}>
                  {activeFaq === idx && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                    >
                      <div className="px-6 pb-6 text-xs text-text-secondary leading-relaxed border-t border-brand-orange/10 pt-4 bg-bg-secondary/20">
                        {faq.a}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* FINAL CTA SECTION */}
      <section id="contact" className="py-24 relative overflow-hidden bg-bg-primary text-white z-10 border-t border-brand-orange/10">
        {/* Animated grid visual */}
        <div className="absolute inset-0 bg-gradient-to-tr from-brand-orange/10 via-transparent to-brand-amber/10 pointer-events-none opacity-60" />
        <div className="absolute inset-0 bg-grid-pattern-40 pointer-events-none" />

        {/* Pulsing Orange/Amber Logistics Supply Chain Connections */}
        <div className="absolute inset-0 opacity-10 pointer-events-none flex items-center justify-center -z-10">
          <svg className="w-[800px] h-[800px] text-brand-amber" viewBox="0 0 200 200" fill="none">
            <circle cx="100" cy="100" r="80" stroke="currentColor" strokeWidth="0.5" strokeDasharray="1 4" />
            <circle cx="100" cy="100" r="50" stroke="currentColor" strokeWidth="0.25" />
            <path d="M40,100 L160,100 M100,40 L100,160" stroke="currentColor" strokeWidth="0.25" />
            <circle cx="40" cy="100" r="2" fill="var(--color-brand-orange)" />
            <circle cx="160" cy="100" r="2" fill="var(--color-brand-orange)" />
            <circle cx="100" cy="40" r="2" fill="var(--color-brand-gold)" />
            <circle cx="100" cy="160" r="2" fill="var(--color-brand-gold)" />
            <path d="M60,60 Q100,90 140,60" stroke="currentColor" strokeWidth="0.5" strokeDasharray="1 1" />
          </svg>
        </div>

        <ScrollReveal className="max-w-4xl mx-auto px-6 relative z-10 text-center flex flex-col gap-6 items-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-wide leading-tight font-title text-text-primary">
            Ready to Transform Your Supply Chain?
          </h2>
          <p className="text-text-secondary text-sm md:text-base leading-relaxed max-w-xl">
            Join organizations using Supply Spot to streamline supplier management, procurement, logistics, and payments.
          </p>

          <div className="flex flex-wrap justify-center items-center gap-4 mt-6">
            <button
              onClick={() => alert("Secure Demo Line Initialized. Booking Dispatch Confirmed.")}
              className="px-8 py-4 font-bold text-xs tracking-wider text-white bg-brand-orange hover:bg-brand-amber rounded-lg shadow-md shadow-brand-orange/15 hover:shadow-brand-amber/30 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300"
            >
              BOOK DEMO
            </button>
            <button
              onClick={() => alert("Connecting direct sales line... Request Dispatched.")}
              className={`px-8 py-4 font-bold text-xs tracking-wider rounded-lg bg-transparent border transition-all duration-300 ${isDark
                  ? "text-slate-200 border-brand-orange/35 hover:border-brand-gold hover:text-brand-gold hover:bg-brand-orange/5"
                  : "text-slate-700 border-slate-200 hover:border-brand-orange hover:text-brand-orange hover:bg-brand-orange/5"
                }`}
            >
              CONTACT SALES
            </button>
          </div>
        </ScrollReveal>
      </section>

      {/* FOOTER */}
      <Footer />
    </div>
  );
}
